import React from 'react';
import { Check } from 'lucide-react';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  description?: string;
  variant?: 'default' | 'card';
}

const Checkbox: React.FC<CheckboxProps> = ({
  label,
  description,
  variant = 'default',
  className = '',
  id,
  checked,
  ...props
}) => {
  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
  
  const baseClasses = 'relative inline-flex items-center justify-center w-5 h-5 rounded-md border-2 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantClasses = {
    default: checked 
      ? 'bg-custom-primary border-custom-primary text-white focus:ring-custom-primary' 
      : 'bg-white border-gray-300 hover:border-custom-primary focus:ring-custom-primary',
    card: checked 
      ? 'bg-custom-secondary border-custom-secondary text-custom-primary focus:ring-custom-secondary' 
      : 'bg-gray-100 border-gray-300 hover:border-custom-secondary focus:ring-custom-secondary'
  };

  const checkboxClasses = `${baseClasses} ${variantClasses[variant]} ${className}`;

  if (!label) {
    return (
      <div className="relative">
        <input
          type="checkbox"
          id={checkboxId}
          checked={checked}
          className="sr-only"
          {...props}
        />
        <label
          htmlFor={checkboxId}
          className={checkboxClasses}
        >
          {checked && (
            <Check 
              size={12} 
              className="absolute inset-0 m-auto" 
              strokeWidth={3}
            />
          )}
        </label>
      </div>
    );
  }

  return (
    <div className="flex items-start space-x-3">
      <div className="relative">
        <input
          type="checkbox"
          id={checkboxId}
          checked={checked}
          className="sr-only"
          {...props}
        />
        <label
          htmlFor={checkboxId}
          className={checkboxClasses}
        >
          {checked && (
            <Check 
              size={12} 
              className="absolute inset-0 m-auto" 
              strokeWidth={3}
            />
          )}
        </label>
      </div>
      
      <div className="flex-1">
        <label 
          htmlFor={checkboxId} 
          className="text-sm font-medium text-gray-900 cursor-pointer"
        >
          {label}
        </label>
        {description && (
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        )}
      </div>
    </div>
  );
};

export default Checkbox;